// src/components/Navbar.js

import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './navbar.css';
import { useAuth } from '../context/AuthContext';
import { useBodyScrollLock } from '../hooks/useBodyScrollLock';
import { useEscapeKey } from '../hooks/useEscapeKey';
import { useViewport } from '../hooks/useViewport';
import { useBrowseChromeCompactState } from '../context/BrowseChromeCompactContext';
import { db } from '../services/firebase';
import { doc, getDoc } from 'firebase/firestore';

function Navbar() {
    const { user, logout } = useAuth();
    const location = useLocation();
    const { isMobile } = useViewport();
    const isBrowseRoute = location.pathname === '/' || location.pathname === '/products';
    const browseCompact = useBrowseChromeCompactState();
    const [menuOpen, setMenuOpen] = useState(false);
    const browseDesktop = isBrowseRoute && !isMobile;
    /** Slim hamburger + cart row (mobile always; desktop browse when scrolled) */
    const compactBar = isMobile || (browseDesktop && browseCompact);
    /** Slide-in drawer panel — mobile always; desktop only when menu open */
    const drawerPanel = isMobile || (compactBar && menuOpen);
    const linksDrawer = isMobile || drawerPanel;
    const [userRoles, setUserRoles] = useState([]);

    useBodyScrollLock(menuOpen, 'navbar-menu-open');

    const closeMenu = useCallback(() => setMenuOpen(false), []);

    const toggleMenu = () => {
        setMenuOpen((prev) => !prev);
    };

    useEffect(() => {
        closeMenu();
    }, [location.pathname, closeMenu]);

    useEscapeKey(menuOpen, closeMenu);

    useEffect(() => {
        if (!compactBar) {
            closeMenu();
        }
    }, [compactBar, closeMenu]);

    useEffect(() => {
        const fetchUserRoles = async () => {
            if (user) {
                try {
                    const userDoc = doc(db, 'users', user.uid);
                    const userSnapshot = await getDoc(userDoc);

                    if (userSnapshot.exists()) {
                        const userData = userSnapshot.data();
                        if (typeof userData.role === 'string') {
                            const roles = userData.role
                                .split(',')
                                .map(role => role.trim())
                                .filter(role => role.length > 0);
                            setUserRoles(roles);
                        } else {
                            setUserRoles([]);
                        }
                    } else {
                        setUserRoles([]);
                    }
                } catch (error) {
                    console.error("Error fetching user role:", error);
                    setUserRoles([]);
                }
            } else {
                setUserRoles([]);
            }
        };

        fetchUserRoles();
    }, [user]);

    const hasRole = roleName => userRoles.includes(roleName);

    const navClassName = [
        'navbar',
        browseDesktop ? 'navbar--browse-desktop' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const hamburgerMorphClass = browseDesktop ? ' hamburger-icon--compact-bar' : '';

    return (
        <nav className={navClassName} aria-label="Main navigation">
            {menuOpen && (
                <button
                    type="button"
                    className="navbar-backdrop"
                    aria-label="Close navigation menu"
                    onClick={closeMenu}
                />
            )}

            <div
                className={`navbar-container${isMobile ? ' navbar-container--drawer' : ''}${browseDesktop ? ' navbar-container--browse-desktop' : ''}`}
            >
                <button
                    type="button"
                    className={`hamburger-icon ${menuOpen ? 'is-open' : ''}${hamburgerMorphClass}`}
                    onClick={toggleMenu}
                    aria-label={menuOpen ? 'Close navigation menu' : 'Open navigation menu'}
                    aria-expanded={menuOpen}
                    aria-controls="navbar-links"
                >
                    <span className="hamburger-lines" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                    </span>
                    <span className="hamburger-close" aria-hidden="true">
                        <span className="hamburger-close__bar" />
                        <span className="hamburger-close__bar" />
                    </span>
                </button>

                <ul
                    id="navbar-links"
                    className={`navbar-links${menuOpen ? ' open' : ''}${linksDrawer ? ' navbar-links--drawer' : ' navbar-links--inline'}`}
                >
                    <li>
                        <Link to={user ? "/" : "/login"} onClick={closeMenu}>Home</Link>
                    </li>
                    <li>
                        <Link to={user ? "/products" : "/login"} onClick={closeMenu}>Products</Link>
                    </li>
                    {user && (
                        <li>
                            <Link to="/account" onClick={closeMenu}>Account</Link>
                        </li>
                    )}
                    <li>
                        <Link to="/contact" onClick={closeMenu}>Contact</Link>
                    </li>
                    {hasRole("Staff") && (
                        <li>
                            <Link to="/staff-dashboard" onClick={closeMenu}>Staff Dashboard</Link>
                        </li>
                    )}
                    {hasRole("Admin") && (
                        <li>
                            <Link to="/admin" onClick={closeMenu}>Admin</Link>
                        </li>
                    )}
                    {hasRole("Admin") && (
                        <li>
                            <Link to="/register" onClick={closeMenu}>Register</Link>
                        </li>
                    )}
                    <li>
                        <Link to={user ? "/cart" : "/login"} onClick={closeMenu}>Cart</Link>
                    </li>
                    {user ? (
                        <li>
                            <Link
                                to="/login"
                                onClick={() => {
                                    logout();
                                    closeMenu();
                                }}
                            >
                                Log Out
                            </Link>
                        </li>
                    ) : (
                        <li>
                            <Link to="/login" onClick={closeMenu}>Login</Link>
                        </li>
                    )}
                </ul>

                <div className="navbar-icons">
                    <Link to={user ? "/cart" : "/login"} aria-label="Cart">
                        <i className="fas fa-shopping-cart" aria-hidden="true" />
                    </Link>
                </div>
            </div>
        </nav>
    );
}

export default Navbar;
